import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Virtuoso } from 'react-virtuoso';
import { VirtuosoTouchPanScroller } from '../../components/virtualized/VirtuosoTouchPanScroller';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChatFill, LikeFill } from '@peacecover/icon';
import { useSceneScrollManager } from './hooks/useSceneScrollManager';
import { getContentKey, useScenePaginationList } from './hooks/useScenePaginationList';
import { useOpenNextPreview } from './hooks/useOpenNextPreview';
import { useSceneDetailDisplay } from './hooks/useSceneDetailDisplay';
import {
  SCENE_READY_POLL_INTERVAL_MS,
  useGetSceneDetail
} from '../../domains/scene/ui/hooks/useGetSceneDetail';
import { useGetAdaptedFictionPreviewDetail } from '../../domains/scene/ui/hooks/useGetAdaptedFictionPreviewDetail';
import { useLikeAdaptedFictionEdition } from '../../domains/scene/ui/hooks/useLikeAdaptedFictionEdition';
import { useRegenerateScene } from '../../domains/scene/ui/hooks/useRegenerateScene';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { usePaymentSuccessMessage } from '../hooks/usePaymentSuccessMessage';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { getCastRelationshipId } from './utils/getCastRelationshipId';
import { getChatDirection } from './utils/getChatDirection';
import { getSceneTags } from './utils/getSceneTags';
import { getThreadSpeakerColor } from './utils/getThreadSpeakerColor';
import ThreadContentItem from './components/ThreadContentItem';
import ChatContentItem from './components/ChatContentItem';
import SceneContentItem from './components/SceneContentItem';
import PreviewHeaderItem from './components/PreviewHeaderItem';
import PreviewCastRelationship from './components/PreviewCastRelationship';
import CommentBottomSheet from './components/CommentBottomSheet';
import SceneOriginalProductList from './components/SceneOriginalProductList';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import MarqueeTitle from '../components/MarqueeTitle';
import OpenNextPreviewCard from './components/OpenNextPreviewCard';
import PreviewGeneratingPopup from './components/PreviewGeneratingPopup';
import { ROUTES } from '../../constants/routes';
import { SCENE_QUERY_KEYS } from '../../domains/scene/ui/queryKeys';
import type { components } from '../../types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];
type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];
type ChatIntroContent = components['schemas']['ApiV1AdaptedFictionChatContent'];
type ThreadIntroContent = components['schemas']['ApiV1AdaptedFictionThreadContent'];
interface StatusBarDetail {
  likesCount: number;
  commentsCount: number;
  isLiked: boolean;
}

const HEART_COUNT_PER_PREVIEW = 1;

const ScenePreviewDetailPage = () => {
  const { sceneId } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const adaptedFictionPreviewId = searchParams.get('adaptedFictionPreviewId');
  const isPreviewMode = !!adaptedFictionPreviewId;
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);
  const [isBettieSheetOpen, setIsBettieSheetOpen] = useState(false);
  const [isGeneratingPopupOpen, setIsGeneratingPopupOpen] = useState(false);

  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const { data: sceneDetail, refetch: refetchSceneDetail } = useGetSceneDetail(sceneId ?? '', {
    refetchInterval
  });

  const isPendingMode =
    !isPreviewMode &&
    (sceneDetail?.status === 'pending' || sceneDetail?.status === 'generating') &&
    !!sceneDetail?.adaptedFictionPreviewId;
  const effectivePreviewMode = isPreviewMode || isPendingMode;
  const effectiveAdaptedFictionPreviewId =
    adaptedFictionPreviewId ?? (isPendingMode ? (sceneDetail?.adaptedFictionPreviewId ?? '') : '');

  const { data: bettieBalances } = useGetBettieBalances();
  const hasEnoughHearts =
    bettieBalances?.bettiePoint?.balance &&
    bettieBalances.bettiePoint.balance >= HEART_COUNT_PER_PREVIEW;

  useEffect(() => {
    if (sceneDetail) {
      if (sceneDetail.status === 'completed' || sceneDetail.status === 'preview_ready') {
        setRefetchInterval(false);
      } else {
        setRefetchInterval(SCENE_READY_POLL_INTERVAL_MS);
      }
    }
  }, [sceneDetail]);

  const { mutateAsync: likeAdaptedFictionEdition } = useLikeAdaptedFictionEdition();
  const { mutate: regenerateScene } = useRegenerateScene();
  const castRelationshipId = getCastRelationshipId();
  const hasRegeneratedRef = useRef(false);

  useEffect(() => {
    if (!hasRegeneratedRef.current && sceneDetail?.status === 'pending' && sceneId) {
      hasRegeneratedRef.current = true;
      regenerateScene(sceneId);
    }
  }, [sceneDetail?.status, sceneId, regenerateScene]);

  useEffect(() => {
    if (
      isPreviewMode &&
      adaptedFictionPreviewId &&
      castRelationshipId &&
      (sceneDetail?.status === 'preview_ready' || sceneDetail?.status === 'completed')
    ) {
      queryClient.invalidateQueries(
        SCENE_QUERY_KEYS.adaptedFictionPreviewDetail(adaptedFictionPreviewId, castRelationshipId)
      );
    }
  }, [
    isPreviewMode,
    adaptedFictionPreviewId,
    castRelationshipId,
    sceneDetail?.status,
    queryClient
  ]);
  const { data: previewDetail } = useGetAdaptedFictionPreviewDetail({
    adaptedFictionPreviewId: effectiveAdaptedFictionPreviewId,
    castRelationshipId,
    enabled: effectivePreviewMode && !!castRelationshipId
  });
  const effectiveScenePreviewId = effectivePreviewMode
    ? effectiveAdaptedFictionPreviewId
    : (sceneDetail?.adaptedFictionPreviewId ?? '');

  const rawContents = previewDetail?.introContents ?? [];
  const introContents =
    previewDetail?.previewType === 'SceneThread'
      ? rawContents.slice(0, 3)
      : rawContents.filter(
          (c): c is ChatIntroContent => 'sequenceGroupNo' in c && c.sequenceGroupNo <= 3
        );

  const paginationOptions = {
    fallbackCursor: effectivePreviewMode ? (previewDetail?.lastContentId ?? null) : null,
    disableBeforeMode: effectivePreviewMode,
    deferToClickOnly: effectivePreviewMode,
    contentsCount: effectivePreviewMode
      ? (previewDetail?.contentsCount ?? null)
      : (sceneDetail?.contentsCount ?? null),
    introContentsLength: effectivePreviewMode ? introContents.length : 0,
    maxGroupSequenceNoFromIntro:
      effectivePreviewMode && introContents.length > 0
        ? previewDetail?.previewType === 'SceneChat'
          ? Math.max(
              ...introContents.map((c) =>
                'sequenceGroupNo' in c ? (c as ChatIntroContent).sequenceGroupNo : 0
              )
            )
          : Math.max(
              ...introContents.map((c) =>
                'sequenceNo' in c ? (c as ThreadIntroContent).sequenceNo : 0
              )
            )
        : 0
  };

  const {
    contents,
    isBeforeMode,
    isLoading,
    isFetchingPreviousPage,
    isFetchingNextOne,
    forwardCursor,
    hasNoMoreContent,
    loadMoreBefore,
    handleOpenNextPreview,
    baseContentsLength,
    remainingPreviewsCount,
    totalCount,
    resetKey
  } = useScenePaginationList({
    sceneId,
    scenePreviewId: effectiveScenePreviewId,
    options: paginationOptions
  });

  const { display } = useSceneDetailDisplay({
    previewDetail,
    sceneDetail,
    contents,
    introContents,
    isPreviewMode: effectivePreviewMode,
    isLoading,
    isBeforeMode
  });
  const hasCursorForNext =
    forwardCursor != null || (effectivePreviewMode && !!previewDetail?.lastContentId);
  const canOpenNextPreview = !hasNoMoreContent && hasCursorForNext;
  const isGenerating =
    sceneDetail?.status !== 'completed' && sceneDetail?.status !== 'preview_ready';
  const shouldDisableOpenNext = (isGenerating ? false : !canOpenNextPreview) || isFetchingNextOne;

  const {
    showStarForIndicesFrom,
    isOpenNextPreviewCardLoading,
    handleOpenNextPreviewWithHeartCheck
  } = useOpenNextPreview({
    handleOpenNextPreview,
    isGenerating,
    hasEnoughHearts: !!hasEnoughHearts,
    contentsLength: contents.length,
    onNeedGeneratingPopup: () => setIsGeneratingPopupOpen(true),
    onNeedBettieSheet: () => setIsBettieSheetOpen(true)
  });

  const { virtuosoRef, hasScrolledRef, markPrependFromBaseLength } = useSceneScrollManager({
    isBeforeMode,
    baseContentsLength,
    isFetchingPreviousPage,
    contentsLength: contents.length
  });

  useEffect(() => {
    void resetKey;
    hasScrolledRef.current = false;
  }, [resetKey, hasScrolledRef]);

  usePaymentSuccessMessage({
    onPaymentSuccess: () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneDetail(sceneId));
    }
  });

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneSubscriptions());
    };
  }, [queryClient]);

  const handleLoadMoreBeforeWithScroll = () => {
    if (!isBeforeMode) return;
    markPrependFromBaseLength();
    hasScrolledRef.current = true;
    loadMoreBefore();
  };

  const getLastContentId = (): string | null => {
    if (contents.length === 0) return null;
    const last = contents[contents.length - 1];
    if ('sceneThreadId' in last && last.sceneThreadId) return last.sceneThreadId;
    if ('sceneChatId' in last && (last as { sceneChatId?: string }).sceneChatId) {
      return (last as { sceneChatId: string }).sceneChatId;
    }
    return null;
  };

  const handleLike = async () => {
    const adaptedFictionEditionId = effectivePreviewMode
      ? previewDetail?.adaptedFictionEditionId
      : sceneDetail?.adaptedFictionEditionId;
    if (!adaptedFictionEditionId) return;
    const lastContentId = effectivePreviewMode
      ? (getLastContentId() ?? previewDetail?.lastContentId ?? null)
      : getLastContentId();
    if (!lastContentId) return;
    try {
      const likeContentType =
        effectivePreviewMode &&
        (previewDetail?.previewType === 'SceneThread' || previewDetail?.previewType === 'SceneChat')
          ? previewDetail.previewType
          : !effectivePreviewMode &&
              (sceneDetail?.contentType === 'SceneThread' ||
                sceneDetail?.contentType === 'SceneChat')
            ? sceneDetail.contentType
            : contents.length > 0 && 'sceneThreadId' in contents[contents.length - 1]
              ? ('SceneThread' as const)
              : ('SceneChat' as const);

      const contexts = [
        { context_type: likeContentType, context_id: lastContentId },
        { context_type: 'CastRelationship' as const, context_id: castRelationshipId }
      ];
      await likeAdaptedFictionEdition({
        adaptedFictionEditionId,
        body: { engagement_contexts: contexts }
      });
      await refetchSceneDetail();
      if (effectivePreviewMode && effectiveAdaptedFictionPreviewId && castRelationshipId) {
        queryClient.invalidateQueries(
          SCENE_QUERY_KEYS.adaptedFictionPreviewDetail(
            effectiveAdaptedFictionPreviewId,
            castRelationshipId
          )
        );
      }
    } catch {
      // 좋아요 실패 시 무시
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden overscroll-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      <Helmet>
        <title>{display.title}</title>
      </Helmet>
      <Header title={display.title} castRelationship={display.detailForHeader} />
      <main className="relative flex flex-1 flex-col min-h-0 bg-[#000] mt-[95px]">
        {display.showIntroContents || (effectivePreviewMode && contents.length > 0) ? (
          <div className="absolute inset-0 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto touch-pan-y hiddenScrollBar overscroll-none pb-[env(safe-area-inset-bottom)]">
            <PreviewHeaderItem
              isR19={display.isR19}
              tags={getSceneTags(display.tagsSource as Parameters<typeof getSceneTags>[0])}
              className="mt-[16px] mb-[16px]"
            />
            {previewDetail?.previewType === 'SceneThread'
              ? (introContents as ThreadIntroContent[]).map((content, index) => (
                  <ThreadContentItem
                    key={`intro-${content.text}-${index}`}
                    speaker={content.speaker ?? undefined}
                    threadType={content.threadType}
                    text={content.text}
                    backgroundColor={getThreadSpeakerColor(
                      display.speakerOrderMap.get(content.speaker ?? '') ?? 0
                    )}
                    hasLine={
                      index < introContents.length - 1 ||
                      contents.length > 0 ||
                      (isPreviewMode && !hasNoMoreContent && remainingPreviewsCount !== 0)
                    }
                    className={content.threadType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
                  />
                ))
              : (introContents as ChatIntroContent[]).map((content, index) => (
                  <ChatContentItem
                    key={`intro-${content.text}-${index}`}
                    chatType={content.chatType}
                    text={content.text}
                    pictureUrl={content.pictureUrl}
                    direction={getChatDirection(display.detailForHeader, content.speaker ?? '')}
                    className={content.chatType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
                    showStar={false}
                  />
                ))}
            {contents.map((content, index) => (
              <SceneContentItem
                key={getContentKey(content)}
                content={content}
                displayContentType={display.contentType}
                index={index}
                contentsLength={contents.length}
                totalCount={totalCount}
                showStarForIndicesFrom={showStarForIndicesFrom}
                speakerOrderMap={display.speakerOrderMap}
                detailForHeader={display.detailForHeader}
              />
            ))}
            {!hasNoMoreContent && remainingPreviewsCount !== 0 && (
              <OpenNextPreviewCard
                remainingPreviewsCount={remainingPreviewsCount}
                speaker={display.lastSpeaker}
                backgroundColor={getThreadSpeakerColor(
                  display.speakerOrderMap.get(display.lastSpeaker ?? '') ?? 0
                )}
                disabled={shouldDisableOpenNext}
                onClick={handleOpenNextPreviewWithHeartCheck}
                isThread={display.contentType === 'SceneThread'}
                isLoading={isOpenNextPreviewCardLoading}
                isGenerating={isGenerating}
              />
            )}
            <SceneOriginalProductList
              sceneId={sceneId ?? ''}
              sceneDetail={sceneDetail ?? null}
              castRelationshipId={castRelationshipId}
              isFullAccessPurchased={sceneDetail?.fullAccessPurchased ?? false}
              castRelationship={sceneDetail?.castRelationship ?? null}
            />
          </div>
        ) : isLoading || contents.length === 0 ? null : (
          <div className="absolute inset-0 min-h-0 min-w-0 overflow-x-hidden touch-pan-y pb-[env(safe-area-inset-bottom)]">
            <Virtuoso
              ref={virtuosoRef}
              overscan={300}
              defaultItemHeight={80}
              style={{ height: '100%' }}
              data={contents}
              startReached={isBeforeMode ? handleLoadMoreBeforeWithScroll : undefined}
              atTopStateChange={
                isBeforeMode ? (atTop) => atTop && handleLoadMoreBeforeWithScroll() : undefined
              }
              atBottomThreshold={isBeforeMode ? undefined : 200}
              computeItemKey={(_index, content: SceneContent) => getContentKey(content)}
              itemContent={(index, content: SceneContent) => (
                <SceneContentItem
                  content={content}
                  displayContentType={display.contentType}
                  index={index}
                  contentsLength={contents.length}
                  totalCount={totalCount}
                  showStarForIndicesFrom={showStarForIndicesFrom}
                  speakerOrderMap={display.speakerOrderMap}
                  detailForHeader={display.detailForHeader}
                />
              )}
              components={{
                Scroller: VirtuosoTouchPanScroller,
                Header: () => (
                  <>
                    {isBeforeMode && isFetchingPreviousPage ? (
                      <div className="px-[22px] pt-[12px]">
                        <BeforeTopLoadingSkeleton />
                      </div>
                    ) : null}
                    {display.hasSequenceOneInList ? (
                      <PreviewHeaderItem
                        isR19={display.isR19}
                        tags={getSceneTags(
                          display.tagsSource as Parameters<typeof getSceneTags>[0]
                        )}
                        className="mt-[16px] mb-[16px]"
                      />
                    ) : null}
                  </>
                ),
                Footer: () => (
                  <>
                    {!hasNoMoreContent && remainingPreviewsCount !== 0 && (
                      <OpenNextPreviewCard
                        remainingPreviewsCount={remainingPreviewsCount}
                        speaker={display.lastSpeaker}
                        backgroundColor={getThreadSpeakerColor(
                          display.speakerOrderMap.get(display.lastSpeaker ?? '') ?? 0
                        )}
                        disabled={shouldDisableOpenNext}
                        onClick={handleOpenNextPreviewWithHeartCheck}
                        isThread={display.contentType === 'SceneThread'}
                        isLoading={isOpenNextPreviewCardLoading}
                        isGenerating={isGenerating}
                      />
                    )}
                    <SceneOriginalProductList
                      sceneId={sceneId ?? ''}
                      sceneDetail={sceneDetail ?? null}
                      castRelationshipId={castRelationshipId}
                      isFullAccessPurchased={sceneDetail?.fullAccessPurchased ?? false}
                      castRelationship={sceneDetail?.castRelationship ?? null}
                    />
                  </>
                )
              }}
            />
          </div>
        )}
      </main>
      <SceneStatusBar
        detail={display.statusBarDetail}
        handleLike={handleLike}
        handleComment={() => setIsCommentSheetOpen(true)}
      />
      {display.effectiveAdaptedFictionEditionId && sceneId && (
        <CommentBottomSheet
          adaptedFictionEditionId={display.effectiveAdaptedFictionEditionId}
          sceneId={sceneId}
          isOpen={isCommentSheetOpen}
          onClose={() => setIsCommentSheetOpen(false)}
          snapToBottom
          isDarkMode={true}
        />
      )}
      <BettieChargeBottomSheet
        isOpen={isBettieSheetOpen}
        onClose={() => setIsBettieSheetOpen(false)}
        onetimeOnly
      />
      {isGeneratingPopupOpen && (
        <PreviewGeneratingPopup
          castRelationship={display.detailForHeader}
          onClose={() => setIsGeneratingPopupOpen(false)}
        />
      )}
    </div>
  );
};

export default ScenePreviewDetailPage;

const Header = ({
  title,
  castRelationship
}: {
  title: string;
  castRelationship: CastRelationship | null;
}) => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  const navigate = useNavigate();
  return (
    <header className="flex flex-col bg-backgroundElevated-dark fixed top-0 left-0 right-0 z-10 pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between w-full px-[16px] py-[10px] relative">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft size={24} className="text-main-dark-1" />
        </button>
        <MarqueeTitle title={title} />
      </div>
      <div className="px-[24px] py-[14px] flex items-center justify-between text-pure-white">
        <PreviewCastRelationship castRelationship={castRelationship} />
        <button
          type="button"
          className="flex items-center gap-[24px]"
          onClick={() => navigate(ROUTES.SCENE.CAST_RELATIONSHIPS)}
        >
          <div className="w-[1px] h-[24px] bg-sub-dark-2" />
          <p className="text-[13px] font-medium leading-[15px]">{t('ScenePreviewDetail.change')}</p>
        </button>
      </div>
    </header>
  );
};

const SceneStatusBar = ({
  detail,
  handleLike,
  handleComment
}: {
  detail: StatusBarDetail | null;
  handleLike: () => void;
  handleComment: () => void;
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-black pt-[16px] rounded-t-[20px] px-[20px] flex items-center justify-start gap-[24px]">
      <button type="button" className="flex flex-col items-center" onClick={handleLike}>
        <LikeFill
          className="text-pure-white"
          gradientStops={
            detail?.isLiked
              ? [
                  { offset: '0%', color: '#FF45A9' },
                  { offset: '100%', color: '#FF6A6A' }
                ]
              : []
          }
        />
        <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
          {detail?.likesCount ?? 0}
        </p>
      </button>
      <button type="button" className="flex flex-col items-center" onClick={handleComment}>
        <ChatFill className="text-pure-white" />
        <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
          {detail?.commentsCount ?? 0}
        </p>
      </button>
    </div>
  );
};

const BeforeTopLoadingSkeleton = () => {
  return (
    <output className="flex gap-[10px] items-stretch py-[15px] animate-pulse" aria-live="polite">
      <div className="flex flex-col items-center gap-[8px]">
        <div className="w-[26px] h-[26px] rounded-full bg-white/10 shrink-0" />
        <div className="w-[2px] flex-1 min-h-[40px] rounded-[16px] bg-white/10" />
      </div>
      <div className="flex flex-col gap-[5px] flex-1 min-w-0">
        <div className="h-[17px] w-[120px] rounded bg-white/10" />
        <div className="h-[19px] w-full rounded bg-white/10" />
        <div className="h-[19px] w-[92%] rounded bg-white/10" />
        <div className="h-[19px] w-[78%] rounded bg-white/10" />
      </div>
    </output>
  );
};
